import {Grid, Hide, Show, Space} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {OfferPriceResponseBody, OfferSaleVehicleResponseBody} from '@dms/api/finance';
import {BusinessCaseResponseBody, SaleBusinessCasePricesOverviewResponseBody} from '@dms/api/sales';
import {SaleVehicleResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePricePermissions} from '@dms/shared';
import {PriceBox} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

interface BusinessCaseSaleVehicleSummaryPricesProps extends TestIdProps {
  saleVehicleData?: SaleVehicleResponseBody;
  saleVehiclePrices?: SaleBusinessCasePricesOverviewResponseBody;
  businessCaseData?: BusinessCaseResponseBody;
  saleVehicle: OfferSaleVehicleResponseBody;
  offerTotalPrice?: OfferPriceResponseBody | null;
  permissions: ReturnType<typeof usePricePermissions>;
  isBrokeredSale: string | null;
  businessCaseLogic: {
    isBrokerage: boolean;
    isOpenBusinessCase: boolean;
    totalMarginPercentage: number;
    isTotalVatDeductible: boolean;
    shouldShowBrokerageContent: boolean;
  };
}

export function BusinessCaseSaleVehicleSummaryPrices(
  props: BusinessCaseSaleVehicleSummaryPricesProps
) {
  const foreignPrice = props.saleVehicleData?.sellingPriceIsForeignCurrency ?? null;

  return (
    <Grid columns={[1, 2, 4]}>
      <Show when={props.permissions.canReadVehicleSellingPrice}>
        <PriceBox
          data-testid={suffixTestId(`sellingPrice`, props)}
          align="left"
          price={props.saleVehiclePrices?.vehicleSellingPrice?.withVat}
          priceSub={props.saleVehiclePrices?.vehicleSellingPrice?.withoutVat}
          isVatDeductible={props.saleVehicleData?.isVatDeductible}
          title={i18n.t`entity.businessCase.labels.vehicleSellingPrice`}
          foreignPrice={foreignPrice}
        />
      </Show>
      <PriceBox
        data-testid={testIds.businessCase.selling('summary-upsell')}
        align="left"
        price={props.saleVehiclePrices?.upsell?.withVat}
        priceSub={props.saleVehiclePrices?.upsell?.withoutVat}
        title={i18n.t('entity.businessCase.labels.upsell')}
      />
      <Hide when={props.saleVehicleData?.type === 'BROKERAGE' || props.isBrokeredSale}>
        <PriceBox
          data-testid={testIds.businessCase.selling('summary-discount')}
          align="left"
          price={
            isNotNilOrEmpty(props.saleVehiclePrices?.discount?.withVat?.amount)
              ? {
                  amount: `-${props.saleVehiclePrices?.discount?.withVat?.amount}`,
                  currency: props.saleVehiclePrices?.discount?.withVat?.currency,
                }
              : undefined
          }
          priceSub={props.saleVehiclePrices?.discount?.withoutVat}
          isVatDeductible={props.saleVehicleData?.isVatDeductible}
          title={i18n.t('general.labels.discount')}
        />
      </Hide>
      <Show when={props.permissions.canReadBusinessCaseSaleTotalPrice}>
        <PriceBox
          data-testid={suffixTestId(`totalPrice`, props)}
          align="left"
          price={props.saleVehiclePrices?.totalPrice?.withVat}
          priceSub={props.saleVehiclePrices?.totalPrice?.withoutVat}
          title={i18n.t`entity.businessCase.labels.totalPrice`}
        />
      </Show>
      <Hide when={props.permissions.canReadVehicleSellingPrice}>
        <Space fillAvailable />
      </Hide>
      <Show when={props.saleVehicleData?.type === 'BROKERAGE' || props.isBrokeredSale}>
        <Space fillAvailable />
      </Show>

      <Show
        when={
          props.permissions.canReadVehicleSellingPrice &&
          props.permissions.canReadVehicleCosts &&
          props.permissions.canReadVehiclePurchasePrice
        }
      >
        <PriceBox
          data-testid={testIds.businessCase.selling('summary-realCosts')}
          align="left"
          price={props.saleVehiclePrices?.realCosts}
          title={i18n.t('entity.businessCase.labels.realCosts')}
          tooltipText={i18n.t('entity.businessCase.labels.realCostsTooltip')}
        />
      </Show>
      <Hide
        when={props.permissions.canReadVehicleSellingPrice && props.permissions.canReadVehicleCosts}
      >
        <Space fillAvailable />
      </Hide>
      <Show when={props.permissions.canReadVehicleEarnings}>
        <PriceBox
          data-testid={testIds.businessCase.selling('summary-totalExpectedEarnings')}
          align="left"
          price={props.saleVehiclePrices?.expectedEarnings}
          title={i18n.t('entity.businessCase.labels.totalExpectedEarnings')}
          tooltipText={i18n.t('entity.businessCase.labels.totalExpectedEarningsTooltip')}
        />
      </Show>
      <Hide when={props.permissions.canReadVehicleEarnings}>
        <Space fillAvailable />
      </Hide>
      <Show
        when={
          props.permissions.canReadVehicleCosts &&
          props.permissions.canReadVehicleSellingPrice &&
          props.permissions.canReadVehiclePurchasePrice &&
          props.permissions.canReadBusinessCaseBrokerageFees
        }
      >
        <Show
          when={
            props.businessCaseLogic.isBrokerage ? props.permissions.canReadVehicleEarnings : true
          }
        >
          <PriceBox
            data-testid={testIds.businessCase.selling('summary-totalGrossMargin')}
            colored
            align="left"
            price={props.saleVehiclePrices?.vehicleMargin?.amount}
            percentTag={Number(props.saleVehiclePrices?.vehicleMargin?.percentage)}
            title={i18n.t('entity.businessCase.labels.grossMarginOnVehicle')}
            tooltipText={i18n.t('entity.businessCase.labels.grossMarginOnVehicleTooltip')}
          />
        </Show>
        <Show when={props.permissions.canReadVehicleEarnings}>
          <PriceBox
            data-testid={testIds.businessCase.selling('summary-grossMarginTotal')}
            title={i18n.t('entity.businessCase.labels.grossMarginTotal')}
            tooltipText={i18n.t('entity.businessCase.labels.grossMarginTotalTooltip')}
            hasAmountSign
            colored
            price={props.saleVehiclePrices?.totalMargin?.amount}
            percentTag={Number(props.saleVehiclePrices?.totalMargin?.percentage)}
          />
        </Show>
      </Show>
      <Hide
        when={
          props.permissions.canReadVehicleCosts &&
          props.permissions.canReadVehicleSellingPrice &&
          props.permissions.canReadVehiclePurchasePrice
        }
      >
        <Hide
          when={
            props.businessCaseLogic.isBrokerage ? props.permissions.canReadVehicleEarnings : true
          }
        >
          <Space fillAvailable />
        </Hide>
        <Hide when={props.permissions.canReadVehicleEarnings}>
          <Space fillAvailable />
        </Hide>
      </Hide>
    </Grid>
  );
}
